import { useState } from 'react';
import { Plus, Edit2, Trash2, BookOpen, Users, Calendar, TrendingUp } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import { formatCurrency, formatDate, generateId } from '../../utils/helpers';

function FeeStructure() {
  const { feeStructures, setFeeStructures, classes, students, feePayments } = useApp();
  const [filterClass, setFilterClass] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name:'', classId:'', academicYear:'2025-2026', dueDate:'', frequency:'annually', components:[{ name:'Tuition Fee', amount:'' }], totalAmount:0 });
  const [editing, setEditing] = useState(null);

  const calcTotal = (components) => components.reduce((s,c)=>s+Number(c.amount||0), 0);

  const openAdd = () => {
    setForm({ name:'', classId:'', academicYear:'2025-2026', dueDate:'', frequency:'annually', components:[{ name:'Tuition Fee', amount:'' }], totalAmount:0 });
    setEditing(null); setModal(true);
  };
  const openEdit = (f) => { setForm(f); setEditing(f); setModal(true); };
  const remove = (id) => { if(window.confirm('Delete fee structure?')) setFeeStructures(prev=>prev.filter(f=>f.id!==id)); };

  const updateComponent = (i, field, val) => {
    const comps = form.components.map((c,idx)=>idx===i?{...c,[field]:val}:c);
    setForm(f=>({...f, components:comps, totalAmount:calcTotal(comps)}));
  };
  const addComponent = () => setForm(f=>({...f, components:[...f.components,{name:'',amount:''}]}));
  const removeComponent = (i) => { const comps=form.components.filter((_,idx)=>idx!==i); setForm(f=>({...f,components:comps,totalAmount:calcTotal(comps)})); };

  const save = () => {
    const data = {...form, totalAmount:calcTotal(form.components)};
    if(editing) setFeeStructures(prev=>prev.map(f=>f.id===editing.id?{...data,id:editing.id}:f));
    else setFeeStructures(prev=>[...prev,{...data,id:generateId('FEE')}]);
    setModal(false);
  };

  const getClass = (id) => { const c=classes.find(c=>c.id===id); return c?`${c.name}-${c.section}`:'-'; };

  // Collection stats per fee structure
  const getStats = (fs) => {
    const enrolled = students.filter(s => s.classId === fs.classId).length;
    const payments = feePayments.filter(p => p.feeStructureId === fs.id);
    const collected = payments.reduce((s,p)=>s+Number(p.amount||0), 0);
    const expected = enrolled * fs.totalAmount;
    const pct = expected > 0 ? Math.round((collected/expected)*100) : 0;
    return { enrolled, collected, expected, pct };
  };

  const academicYears = [...new Set(feeStructures.map(f=>f.academicYear))].sort().reverse();

  const filtered = feeStructures.filter(fs =>
    (filterClass === 'all' || fs.classId === filterClass) &&
    (filterYear === 'all' || fs.academicYear === filterYear)
  );

  const freqColor = { monthly:'bg-blue-100 text-blue-700', quarterly:'bg-purple-100 text-purple-700', annually:'bg-green-100 text-green-700' };

  const inputCls = 'w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Fee Structure</h1>
          <p className="text-gray-500 text-sm mt-0.5">{filtered.length} structure{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterYear} onChange={e=>setFilterYear(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="all">All Years</option>
            {academicYears.map(y=><option key={y}>{y}</option>)}
          </select>
          <select value={filterClass} onChange={e=>setFilterClass(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="all">All Classes</option>
            {classes.map(c=><option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
          <button onClick={openAdd} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors">
            <Plus size={16}/>Add Structure
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid gap-3">
        {filtered.length === 0 && (
          <div className="text-center py-10">
            <BookOpen size={36} className="mx-auto mb-2 opacity-30"/>
            <p className="text-sm">No fee structures found</p>
          </div>
        )}
        {filtered.map(fs => {
          const stats = getStats(fs);
          const total = fs.totalAmount || 0;
          return (
            <div key={fs.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="flex items-start justify-between px-4 py-2 border-b border-gray-100">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="font-semibold">{fs.name}</h3>
                    <span className={`font-medium px-2 py-0.5 rounded-full ${freqColor[fs.frequency] || 'bg-gray-100'}`}>
                      {fs.frequency}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1"><BookOpen size={12}/>{getClass(fs.classId)}</span>
                    <span>{fs.academicYear}</span>
                    {fs.dueDate && <span className="flex items-center gap-1"><Calendar size={12}/>Due: {formatDate(fs.dueDate)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={()=>openEdit(fs)} className="p-1 hover:bg-indigo-50 rounded-lg text-indigo-600"><Edit2 size={14}/></button>
                  <button onClick={()=>remove(fs.id)} className="p-1 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={14}/></button>
                </div>
              </div>

              <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Fee Breakdown Table */}
                <div>
                  <h4 className="mb-2">Fee Breakdown</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left">
                        <th className="font-medium pb-1">Component</th>
                        <th className="font-medium pb-1 text-right">Amount</th>
                        <th className="font-medium pb-1 text-right w-24">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fs.components||[]).map((comp,i)=>{
                        const pct = total > 0 ? Math.round((Number(comp.amount)/total)*100) : 0;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            <td className="py-1">{comp.name}</td>
                            <td className="py-1 font-medium text-right">{formatCurrency(comp.amount)}</td>
                            <td className="py-1 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-gray-100 rounded-full h-1.5">
                                  <div className="bg-indigo-400 h-1.5 rounded-full" style={{width:`${pct}%`}}/>
                                </div>
                                <span className="w-8 text-right">{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-200">
                        <td className="pt-2 font-semibold">Total</td>
                        <td className="pt-2 font-bold text-indigo-600 text-right">{formatCurrency(total)}</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Collection Stats */}
                <div className="border-t lg:border-t-0 lg:border-l border-gray-100 pt-3 lg:pt-0 lg:pl-4">
                  <h4 className="mb-2">Collection Status</h4>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1"><Users size={12}/>Enrolled</span>
                      <span className="font-semibold">{stats.enrolled} students</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="">Expected</span>
                      <span className="font-semibold">{formatCurrency(stats.expected)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1"><TrendingUp size={12}/>Collected</span>
                      <span className="font-semibold text-green-600">{formatCurrency(stats.collected)}</span>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span>Collection rate</span>
                        <span>{stats.pct}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${stats.pct >= 80 ? 'bg-green-500' : stats.pct >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}
                          style={{width:`${Math.min(stats.pct,100)}%`}}
                        />
                      </div>
                    </div>
                    {stats.expected > stats.collected && (
                      <div className="bg-orange-50 rounded px-2 py-0.5">
                        <p className="text-orange-600 font-medium">Pending: {formatCurrency(stats.expected - stats.collected)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} onClose={()=>setModal(false)} title={editing?'Edit Fee Structure':'Add Fee Structure'} size="lg">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Structure Name</label>
            <input value={form.name||''} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className={inputCls} placeholder="e.g. Class 6 Annual Fee 2025-26" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Class</label>
              <select value={form.classId||''} onChange={e=>setForm(f=>({...f,classId:e.target.value}))} className={inputCls}>
                <option value="">Select Class</option>
                {classes.map(c=><option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Academic Year</label>
              <input value={form.academicYear||''} onChange={e=>setForm(f=>({...f,academicYear:e.target.value}))} className={inputCls} placeholder="e.g. 2025-2026" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Frequency</label>
              <select value={form.frequency||'annually'} onChange={e=>setForm(f=>({...f,frequency:e.target.value}))} className={inputCls}>
                {['monthly','quarterly','annually'].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <input type="date" value={form.dueDate||''} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} className={inputCls} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Fee Components</label>
              <button onClick={addComponent} className="text-indigo-600 hover:text-indigo-800 font-medium">+ Add Component</button>
            </div>
            <div className="space-y-2">
              {form.components.map((comp,i)=>(
                <div key={i} className="flex gap-2 items-center">
                  <input placeholder="Component name" value={comp.name||''} onChange={e=>updateComponent(i,'name',e.target.value)} className="flex-1 px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                  <input type="number" placeholder="Amount ₹" value={comp.amount||''} onChange={e=>updateComponent(i,'amount',e.target.value)} className="w-32 px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                  {form.components.length > 1 && <button onClick={()=>removeComponent(i)} className="text-red-400 hover:text-red-600 p-1">✕</button>}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-indigo-50 rounded-lg px-4 py-1 flex justify-between items-center">
            <span className="text-sm font-medium text-indigo-700">Total Amount</span>
            <span className="text-base font-bold text-indigo-700">{formatCurrency(calcTotal(form.components))}</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={()=>setModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
        </div>
      </Modal>
    </div>
  );
}
export default FeeStructure;
